import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import type { ActivityType } from '@molecule/app-ide-react'
import {
  ACTIVITY_TYPES,
  activityStatusColors,
  activityStatusLabel,
  activitySummaryLine,
  activityTypeLabel,
} from '@molecule/app-ide-react'
import { useHttpClient, useTranslation } from '@molecule/app-react'
import { getClassMap } from '@molecule/app-ui'
import { Button, Flex, Tooltip } from '@molecule/app-ui-react'

import { useIsMobile } from '../hooks/useMediaQuery.js'
import {
  activityEmptyStateKind,
  activityIconName,
  activityRequestError,
  type ActivityScopeFilter,
  activityScopeLabel,
  activityTruncation,
  type CapturedActivity,
  detailMode,
  formatActivityAge,
  formatActivityTimestamp,
  formatBytes,
  formatPayload,
  mergeActivities,
} from '../utilities/activity.js'
import { Icon } from './Icon.js'
import { MoleculeSpinner } from './MoleculeSpinner.js'
import { usePanelModalTabs } from './PanelModal.js'
import { UsageInspector } from './UsageInspector.js'

/**
 * Default auto-refresh cadence (ms) while the Activity view is open. Captured
 * side effects POST to molecule-dev on their own request and are only fanned out
 * over an *active* Synthase chat stream (`stream.ts`); when the panel is open
 * without a live turn there is no push channel, so the panel polls to stay live.
 *
 * The poll is a `?since=<newest row>` request, so its steady-state response is
 * an empty array — it used to re-transfer the project's ENTIRE capture history,
 * payload bodies included, every four seconds.
 */
export const DEFAULT_ACTIVITY_POLL_MS = 4000

/** Rows fetched per page (the server caps this independently). */
export const ACTIVITY_PAGE_SIZE = 50

/**
 * Per-channel "how to trigger one" hints, shown under the empty state when the
 * app HAS a provider for the channel — so a user can verify capture is working
 * at a glance (A-01: "how can i verify that each thing here is working?").
 */
const WIRED_HINT_DEFAULTS: Record<ActivityType, string> = {
  email:
    'Trigger any email in your app — like a sign-up or password reset — and it appears here within a few seconds.',
  sms: 'Trigger any SMS in your app — like a verification code — and it appears here within a few seconds.',
  push: 'Trigger a push notification in your app and it appears here within a few seconds.',
  webhook: 'Trigger any outbound webhook in your app and it appears here within a few seconds.',
  channel:
    'Trigger a channel message in your app — like a Slack or Discord post — and it appears here within a few seconds.',
}

/**
 * Per-channel copy for channels the app has NO provider for: the tab isn't
 * broken, capture just activates once a provider is added.
 */
const UNWIRED_HINT_DEFAULTS: Record<ActivityType, string> = {
  email: 'Your app has no email provider yet, so nothing is captured. Ask Synthase to add one.',
  sms: 'Your app has no SMS provider yet, so nothing is captured. Ask Synthase to add one.',
  push: 'Your app has no push notification provider yet, so nothing is captured. Ask Synthase to add one.',
  webhook: 'Your app has no webhook provider yet, so nothing is captured. Ask Synthase to add one.',
  channel:
    'Your app has no chat channel provider yet, so nothing is captured. Ask Synthase to add one.',
}

/** The list response shape (see `App/Resource/Activity/index.ts` `list`). */
interface ActivityListResponse {
  activities: CapturedActivity[]
  count: number
  total?: number
  hasMore: boolean
  nextCursor: string | null
  limit: number
  wiredTypes?: ActivityType[]
}

/**
 * Props for the Activity panel.
 */
interface ActivityPanelProps {
  /** False for read-only viewers: delete / clear / send-to-inbox are hidden. */
  canEdit?: boolean
  /** Project whose captured activities to list. */
  projectId: string
  /** When set, pre-selects this channel-filter tab (e.g. opened from an inline card). */
  initialType?: ActivityType | null
  /** When set, auto-expands the row for this activity id on mount. */
  initialActivityId?: string | null
  /**
   * Auto-refresh cadence in ms while the Activity view is open. Defaults to
   * {@link DEFAULT_ACTIVITY_POLL_MS}. Exposed so callers (and tests) can tune
   * the live cadence.
   */
  pollIntervalMs?: number
}

/** Shared style for the small pill-shaped filter buttons. */
const pillStyle = (isActive: boolean, isMobile: boolean) => ({
  display: 'inline-flex' as const,
  alignItems: 'center' as const,
  gap: 5,
  fontSize: 12,
  padding: '4px 10px',
  // Touch floor on phones; desktop keeps the compact pill.
  minHeight: isMobile ? 40 : undefined,
  // A pill label never wraps — the row wraps whole pills.
  whiteSpace: 'nowrap' as const,
  borderRadius: 6,
  cursor: 'pointer' as const,
  // Same selected-pill treatment as every other filter strip in the IDE panels
  // (DatabasePanel's ACTIVE_PILL, LogsPanel's segButtonStyle, StoragePanel):
  // a 40% primary border over a 15% primary wash, mixed from the THEME token.
  // It used to be a hardcoded `rgba(99,102,241,0.12)`, which drifted off-hue the
  // moment the primary token changed and ignored the theme entirely.
  border: isActive
    ? '1px solid color-mix(in srgb, var(--color-primary) 40%, transparent)'
    : '1px solid var(--color-border-secondary)',
  background: isActive
    ? 'color-mix(in srgb, var(--color-primary) 15%, transparent)'
    : 'transparent',
  color: isActive ? 'var(--color-primary)' : 'inherit',
  transition: 'background 100ms, border-color 100ms',
})

/**
 * Renders the expanded detail of one activity: email as rendered HTML/text,
 * SMS body, or the raw JSON payload/result for everything else. When the API
 * had to cut an oversized payload down, says so instead of rendering a box the
 * user can't explain.
 * @param root0 - Component props.
 * @param root0.activity - The activity to render in detail.
 * @returns The rendered detail element.
 */
function ActivityDetail({ activity }: { activity: CapturedActivity }) {
  const { t } = useTranslation()
  const payload = activity.payload as Record<string, unknown> | undefined
  const mode = detailMode(activity)
  const cut = activityTruncation(activity.payload) ?? activityTruncation(activity.result)

  const notice = cut ? (
    <div
      data-mol-id={`activity-truncated-${activity.id}`}
      style={{
        marginBottom: 8,
        padding: '6px 8px',
        borderRadius: 6,
        border: '1px solid var(--color-border-secondary)',
        fontSize: 11.5,
        lineHeight: 1.45,
        color: 'var(--color-foreground-tertiary)',
      }}
    >
      {t(
        'activity.truncated',
        { size: formatBytes(cut.originalBytes) },
        {
          defaultValue: `This capture was {{size}} — too large to store in full, so it was shortened.`,
        },
      )}
      {cut.droppedFields.length > 0 &&
        ` ${t(
          'activity.truncatedDropped',
          { fields: cut.droppedFields.join(', ') },
          { defaultValue: 'Removed: {{fields}}.' },
        )}`}
    </div>
  ) : null

  // Email: prefer a rendered HTML body, then a text body.
  if (mode === 'email-html' || mode === 'email-text') {
    const html = typeof payload?.html === 'string' ? payload.html : undefined
    const text = typeof payload?.text === 'string' ? payload.text : undefined
    const subject = typeof payload?.subject === 'string' ? payload.subject : undefined
    return (
      <div data-mol-id={`activity-detail-${activity.id}`}>
        {notice}
        {subject && (
          <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>
            {t('activity.detail.subject', { subject }, { defaultValue: `Subject: ${subject}` })}
          </div>
        )}
        {mode === 'email-html' && html ? (
          // Captured email HTML is attacker-controllable (a sandbox tenant POSTs it
          // to the activity API with its project token), and the IDE runs on the
          // PRIVILEGED control-plane origin — so it must NEVER be injected via
          // dangerouslySetInnerHTML (P4FE-01). Render it in a fully sandboxed iframe:
          // a bare `sandbox` attribute (no allow-scripts, no allow-same-origin) makes
          // all script/markup inert and isolates it from the IDE origin + localStorage,
          // while still rendering styled HTML for the preview.
          <iframe
            title={t('activity.detail.emailPreview', undefined, { defaultValue: 'Email preview' })}
            sandbox=""
            srcDoc={html}
            style={{
              display: 'block',
              width: '100%',
              border: '1px solid var(--color-border)',
              borderRadius: 6,
              background: 'var(--color-surface)',
              height: 280,
            }}
          />
        ) : (
          <pre
            style={{
              margin: 0,
              whiteSpace: 'pre-wrap',
              fontSize: 12,
              fontFamily: 'var(--mol-font-mono, monospace)',
            }}
          >
            {text ?? ''}
          </pre>
        )}
      </div>
    )
  }

  // SMS / channel: show the message body.
  if (mode === 'message-body') {
    const body =
      (typeof payload?.message === 'string' && payload.message) ||
      (typeof payload?.body === 'string' && payload.body) ||
      ''
    return (
      <div data-mol-id={`activity-detail-${activity.id}`}>
        {notice}
        <pre
          style={{
            margin: 0,
            whiteSpace: 'pre-wrap',
            fontSize: 13,
            fontFamily: 'var(--mol-font-mono, monospace)',
          }}
        >
          {body}
        </pre>
      </div>
    )
  }

  // Webhook + fallback: raw JSON payload and result.
  const empty = activity.payload == null && activity.result == null
  return (
    <div data-mol-id={`activity-detail-${activity.id}`}>
      {notice}
      {empty && (
        <div style={{ fontSize: 12, color: 'var(--color-foreground-tertiary)' }}>
          {t('activity.detail.noBody', undefined, {
            defaultValue: 'This capture recorded no body — only the summary above.',
          })}
        </div>
      )}
      {activity.payload != null && (
        <>
          <div style={{ fontSize: 11, opacity: 0.6, marginBottom: 2 }}>
            {t('activity.detail.payload', undefined, { defaultValue: 'Payload' })}
          </div>
          <pre
            style={{
              margin: '0 0 8px',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              fontSize: 12,
              fontFamily: 'var(--mol-font-mono, monospace)',
              maxHeight: 220,
              overflow: 'auto',
            }}
          >
            {formatPayload(activity.payload)}
          </pre>
        </>
      )}
      {activity.result != null && (
        <>
          <div style={{ fontSize: 11, opacity: 0.6, marginBottom: 2 }}>
            {t('activity.detail.result', undefined, { defaultValue: 'Result' })}
          </div>
          <pre
            style={{
              margin: 0,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              fontSize: 12,
              fontFamily: 'var(--mol-font-mono, monospace)',
              maxHeight: 160,
              overflow: 'auto',
            }}
          >
            {formatPayload(activity.result)}
          </pre>
        </>
      )}
    </div>
  )
}

/**
 * Activity panel — lists a project's captured outbound side effects
 * (email/sms/push/webhook/channel) from `GET /projects/:id/activities`, with
 * per-channel and per-environment filters, per-row expand-to-detail, delete,
 * export and a "send to my inbox" action on email rows.
 *
 * Dev/prod: a dev sandbox INTERCEPTS a side effect (recorded, never sent) while
 * a deployed app TEES it (really sent, then recorded). Both land in this one
 * list, each row stamped with the environment its runtime token proved, so the
 * two are never confused for one another.
 *
 * The list is a bounded page (`limit`/`cursor`), and the open panel polls with
 * `?since=` so the steady state transfers nothing.
 * @param root0 - Component props.
 * @param root0.projectId - Project whose activities to list.
 * @param root0.initialType - Channel-filter tab to pre-select.
 * @param root0.initialActivityId - Activity row to auto-expand on mount.
 * @param root0.pollIntervalMs - Auto-refresh cadence in ms.
 * @returns The rendered Activity panel.
 */
export function ActivityPanel({
  canEdit,
  projectId,
  initialType,
  initialActivityId,
  pollIntervalMs = DEFAULT_ACTIVITY_POLL_MS,
}: ActivityPanelProps) {
  const cm = getClassMap()
  const { t } = useTranslation()
  const http = useHttpClient()
  // Touch floor for the small controls on phones (desktop keeps compact sizes).
  const isMobile = useIsMobile()
  // Top-level view: captured side effects ('activity') vs brokered-service
  // usage ('usage') — same surface, per docs/BAAS-activity.md §5.
  const [view, setView] = useState<'activity' | 'usage'>('activity')

  // Top-level view tabs — Activity (side effects) vs Usage (service spend) —
  // render in the MODAL HEADER as the shared segmented control
  // (usePanelModalTabs), one tab idiom across every workspace modal.
  usePanelModalTabs({
    tabs: [
      {
        id: 'activity',
        label: t('activity.view.activity', undefined, { defaultValue: 'Activity' }),
        molId: 'activity-view-activity',
      },
      {
        id: 'usage',
        label: t('activity.view.usage', undefined, { defaultValue: 'Usage' }),
        molId: 'activity-view-usage',
      },
    ],
    activeId: view,
    onSelect: (id) => setView(id as 'activity' | 'usage'),
    label: t('activity.view.label', undefined, { defaultValue: 'View' }),
    molId: 'activity-view-tabs',
  })
  const [activities, setActivities] = useState<CapturedActivity[]>([])
  const [loading, setLoading] = useState(true)
  // A background poll / manual refresh is in flight (distinct from the initial
  // `loading` so the list stays on screen instead of flashing the spinner).
  const [refreshing, setRefreshing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [activeType, setActiveType] = useState<ActivityType | null>(initialType ?? null)
  const [activeScope, setActiveScope] = useState<ActivityScopeFilter>(null)
  const [expandedId, setExpandedId] = useState<string | null>(initialActivityId ?? null)
  const [sendingId, setSendingId] = useState<string | null>(null)
  // Per-activity send-to-inbox failure messages (keyed by activity id) so a
  // failed send surfaces inline instead of being swallowed.
  const [sendErrors, setSendErrors] = useState<Record<string, string>>({})
  // Which channels the app is actually wired to produce. Comes from the LIST
  // response (derived server-side from `projects.packages`), so choosing an
  // empty-state sentence costs no sandbox round trip and works while the
  // sandbox sleeps; null while unknown.
  const [wiredTypes, setWiredTypes] = useState<ReadonlySet<ActivityType> | null>(null)
  // Paging + totals.
  const [nextCursor, setNextCursor] = useState<string | null>(null)
  const [hasMore, setHasMore] = useState(false)
  const [total, setTotal] = useState<number | null>(null)
  const [loadingMore, setLoadingMore] = useState(false)
  // Destructive + export controls.
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [confirmingClear, setConfirmingClear] = useState(false)
  const [clearing, setClearing] = useState(false)
  const [exporting, setExporting] = useState(false)
  // A relative-age label has to re-render to stay true; one clock for the list.
  const [nowMs, setNowMs] = useState(() => Date.now())

  // The newest row we hold, so the poll can ask for only what is newer. A ref
  // (not a dep) keeps the poll effect from re-subscribing on every new row.
  const newestRef = useRef<string | null>(null)
  useEffect(() => {
    newestRef.current = activities[0]?.timestamp ?? null
  }, [activities])
  // A request is already out. The poll skips its tick rather than stacking a
  // second one on a slow connection — two in flight can also complete out of
  // order, writing a stale list back over a newer one.
  const inFlightRef = useRef(false)

  // Re-sync filter/expansion when opened from a different inline card.
  useEffect(() => {
    if (initialType !== undefined) setActiveType(initialType ?? null)
  }, [initialType])
  useEffect(() => {
    if (initialActivityId) setExpandedId(initialActivityId)
  }, [initialActivityId])

  /** Filter params shared by every request this panel makes. */
  const filterParams = useMemo(() => {
    const params: Record<string, string> = {}
    if (activeType) params.type = activeType
    if (activeScope) params.environment = activeScope
    return params
  }, [activeType, activeScope])

  const loadActivities = useCallback(
    async ({ silent = false }: { silent?: boolean } = {}) => {
      // Initial / filter-change loads show the spinner; background polls and the
      // manual Refresh keep the current list on screen and only flag `refreshing`.
      if (silent) setRefreshing(true)
      else setLoading(true)
      inFlightRef.current = true
      try {
        const res = await http.get<ActivityListResponse>(`/projects/${projectId}/activities`, {
          params: { ...filterParams, limit: ACTIVITY_PAGE_SIZE, wired: '1' },
        })
        const rows = res.data?.activities ?? []
        setActivities(rows)
        // Set eagerly, not only from the effect below: the effect runs a commit
        // later, and a poll tick landing in that gap would fall back to a FULL
        // fetch because it could not see a newest row to ask from.
        newestRef.current = rows[0]?.timestamp ?? null
        setNextCursor(res.data?.nextCursor ?? null)
        setHasMore(res.data?.hasMore ?? false)
        setTotal(res.data?.total ?? null)
        if (res.data?.wiredTypes) setWiredTypes(new Set(res.data.wiredTypes))
        setError(null)
      } catch (error) {
        console.error('Failed to fetch activities', error)
        setError(t('activity.loadError', undefined, { defaultValue: 'Failed to load activity.' }))
      }
      inFlightRef.current = false
      if (silent) setRefreshing(false)
      else setLoading(false)
    },
    [http, projectId, filterParams, t],
  )

  /**
   * The cheap poll: ask only for rows newer than the newest one on screen and
   * merge them in. Falls back to a full page when the list is empty (there is
   * no "newest" to ask from).
   */
  const pollActivities = useCallback(async () => {
    // A load is already out — skip this tick rather than stack a second request
    // (and risk the older one landing last).
    if (inFlightRef.current) return
    const since = newestRef.current
    if (!since) {
      await loadActivities({ silent: true })
      return
    }
    setRefreshing(true)
    inFlightRef.current = true
    try {
      const res = await http.get<ActivityListResponse>(`/projects/${projectId}/activities`, {
        params: { ...filterParams, limit: ACTIVITY_PAGE_SIZE, since },
      })
      const incoming = res.data?.activities ?? []
      if (incoming.length > 0) {
        setActivities((prev) => mergeActivities(prev, incoming))
        // Newest-first from the server, so its head is the new high-water mark.
        newestRef.current = incoming[0]?.timestamp ?? newestRef.current
        setTotal((prev) => (prev == null ? prev : prev + incoming.length))
      }
      setError(null)
    } catch (error) {
      console.error('Failed to poll activities', error)
    }
    inFlightRef.current = false
    setRefreshing(false)
  }, [http, projectId, filterParams, loadActivities])

  // Initial load + reload when a filter changes.
  useEffect(() => {
    loadActivities()
  }, [loadActivities])

  // Live updates: captured side effects arrive on their own POST and are only
  // pushed inline during an active chat turn (stream.ts), so the open panel
  // polls to stay current whether or not a turn is running. Activity view only
  // (the Usage view runs its own poll), and paused while the tab is hidden.
  useEffect(() => {
    if (view !== 'activity' || pollIntervalMs <= 0) return
    const id = setInterval(() => {
      if (typeof document !== 'undefined' && document.hidden) return
      setNowMs(Date.now())
      void pollActivities()
    }, pollIntervalMs)
    return () => clearInterval(id)
  }, [view, pollIntervalMs, pollActivities])

  const loadMore = useCallback(async () => {
    if (!nextCursor) return
    setLoadingMore(true)
    try {
      const res = await http.get<ActivityListResponse>(`/projects/${projectId}/activities`, {
        params: { ...filterParams, limit: ACTIVITY_PAGE_SIZE, cursor: nextCursor },
      })
      const older = res.data?.activities ?? []
      setActivities((prev) => mergeActivities(prev, older))
      setNextCursor(res.data?.nextCursor ?? null)
      setHasMore(res.data?.hasMore ?? false)
    } catch (error) {
      console.error('Failed to load older activity', error)
      setError(t('activity.loadError', undefined, { defaultValue: 'Failed to load activity.' }))
    }
    setLoadingMore(false)
  }, [http, projectId, filterParams, nextCursor, t])

  // Belt-and-braces client filter (the server already filters when activeType
  // is set). Inline rather than the shared `filterActivitiesByType`, which is
  // declared over the base `Activity` shape and would widen away the two
  // molecule-dev columns (`environment`, `inboxSentAt`) the rows below render.
  const visible = useMemo(
    () => (activeType ? activities.filter((row) => row.type === activeType) : activities),
    [activities, activeType],
  )

  // Which empty state to render for the current tab: wired (hint how to
  // trigger one), unwired (say capture activates once a provider is added), or
  // unknown (generic copy while the package list is unavailable).
  const emptyKind = activityEmptyStateKind(activeType, wiredTypes)
  const emptyHint =
    emptyKind === 'unknown'
      ? null
      : activeType === null
        ? emptyKind === 'wired'
          ? t('activity.emptyHint.all', undefined, {
              defaultValue:
                'Trigger any captured action in your app — an email, SMS, push, or webhook — and it appears here within a few seconds.',
            })
          : t('activity.notWired.all', undefined, {
              defaultValue:
                'Your app has no providers for these channels yet, so nothing is captured. Ask Synthase to add one.',
            })
        : emptyKind === 'wired'
          ? t(`activity.emptyHint.${activeType}`, undefined, {
              defaultValue: WIRED_HINT_DEFAULTS[activeType],
            })
          : t(`activity.notWired.${activeType}`, undefined, {
              defaultValue: UNWIRED_HINT_DEFAULTS[activeType],
            })

  const handleSendToInbox = useCallback(
    async (activity: CapturedActivity) => {
      setSendingId(activity.id)
      setSendErrors((prev) => {
        if (!(activity.id in prev)) return prev
        const next = { ...prev }
        delete next[activity.id]
        return next
      })
      try {
        // Re-send the captured email to the owner's own inbox (self-addressed
        // — the abuse control). Backed by POST .../send-to-inbox in
        // App/Resource/Activity/index.ts.
        const res = await http.post<{ ok?: boolean; sentTo?: string; sentAt?: string }>(
          `/projects/${projectId}/activities/${activity.id}/send-to-inbox`,
        )
        // Reflect what the SERVER said, not the fact that the request returned:
        // the send is recorded on the row, so the state survives a panel close
        // and a reload instead of resetting to "not sent".
        if (res.data?.ok) {
          const sentAt = res.data.sentAt ?? new Date().toISOString()
          setActivities((prev) =>
            prev.map((row) => (row.id === activity.id ? { ...row, inboxSentAt: sentAt } : row)),
          )
        }
      } catch (error) {
        // Surface the failure instead of swallowing it — the endpoint returns an
        // actionable `errorKey` (no email transport, no owner email, …). The
        // button stays enabled so the user can retry.
        console.error('Send-to-inbox failed', error)
        const { errorKey } = activityRequestError(error)
        const message =
          errorKey === 'activity.error.noEmailTransport'
            ? t('activity.sendToInbox.noTransport', undefined, {
                defaultValue: 'No email transport is configured for this project yet.',
              })
            : errorKey === 'activity.error.noOwnerEmail'
              ? t('activity.sendToInbox.noOwnerEmail', undefined, {
                  defaultValue: 'Your account has no email address on file.',
                })
              : errorKey === 'activity.error.noPayload'
                ? t('activity.sendToInbox.noPayload', undefined, {
                    defaultValue: 'This capture has no message body to send.',
                  })
                : t('activity.sendToInbox.error', undefined, {
                    defaultValue: 'Could not send to your inbox. Please try again.',
                  })
        setSendErrors((prev) => ({ ...prev, [activity.id]: message }))
      } finally {
        setSendingId(null)
      }
    },
    [http, projectId, t],
  )

  const handleDelete = useCallback(
    async (activity: CapturedActivity) => {
      setDeletingId(activity.id)
      try {
        await http.delete(`/projects/${projectId}/activities/${activity.id}`)
        setActivities((prev) => prev.filter((row) => row.id !== activity.id))
        setTotal((prev) => (prev == null ? prev : Math.max(0, prev - 1)))
        if (expandedId === activity.id) setExpandedId(null)
      } catch (error) {
        console.error('Failed to delete activity', error)
        setError(
          t('activity.deleteError', undefined, { defaultValue: 'Could not delete that capture.' }),
        )
      }
      setDeletingId(null)
    },
    [http, projectId, expandedId, t],
  )

  const handleClear = useCallback(async () => {
    setClearing(true)
    try {
      await http.delete(`/projects/${projectId}/activities`, { params: filterParams })
      setConfirmingClear(false)
      await loadActivities()
    } catch (error) {
      console.error('Failed to clear activity', error)
      setError(
        t('activity.clearError', undefined, { defaultValue: 'Could not clear captured activity.' }),
      )
    }
    setClearing(false)
  }, [http, projectId, filterParams, loadActivities, t])

  const handleExport = useCallback(async () => {
    setExporting(true)
    try {
      const res = await http.get<unknown>(`/projects/${projectId}/activities/export`, {
        params: filterParams,
      })
      const blob = new Blob([JSON.stringify(res.data ?? {}, null, 2)], {
        type: 'application/json',
      })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `molecule-activity-${projectId}.json`
      document.body.appendChild(link)
      link.click()
      link.remove()
      URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Failed to export activity', error)
      setError(
        t('activity.exportError', undefined, {
          defaultValue: 'Could not export captured activity.',
        }),
      )
    }
    setExporting(false)
  }, [http, projectId, filterParams, t])

  return (
    // Fills the PanelModal (the DeployPanel/LogsPanel idiom) instead of a fixed
    // self-cap — a 400px cap inside the phone's full-screen modal left a nested
    // double-scroll and most of the screen empty.
    <div
      style={{ padding: 16, height: '100%', boxSizing: 'border-box', overflow: 'auto' }}
      data-mol-id="activity-panel"
    >
      {/* The "Activity" title lives in the modal chrome and the Activity/Usage
          view tabs in the modal header (usePanelModalTabs above) — no
          duplicate heading or tab bar here. */}

      {/* Scope note. Both views span dev AND production, and each row says which
          it was; the old note claimed Activity was dev-only and Usage was
          deployed-only, and neither was true. */}
      <div
        data-mol-id="activity-scope-note"
        data-scope="both"
        style={{
          marginBottom: 12,
          fontSize: 11.5,
          lineHeight: 1.45,
          color: 'var(--color-foreground-tertiary)',
        }}
      >
        {view === 'activity'
          ? t('activity.scope.both', undefined, {
              defaultValue:
                'Dev captures are recorded instead of sent. Production sends are real, and recorded after they go out. Each row says which.',
            })
          : t('activity.scope.usage', undefined, {
              defaultValue:
                'Brokered-service usage for this project, from your sandbox and your deployed app.',
            })}
      </div>

      {view === 'usage' ? (
        <UsageInspector projectId={projectId} />
      ) : (
        <>
          {/* Channel filter tabs */}
          <Flex align="center" gap="xs" wrap="wrap" style={{ marginBottom: 8 }}>
            {([null, ...ACTIVITY_TYPES] as Array<ActivityType | null>).map((type) => {
              const isActive = activeType === type
              const label =
                type === null
                  ? t('activity.filter.all', undefined, { defaultValue: 'All' })
                  : activityTypeLabel(type)
              return (
                <button
                  key={type ?? 'all'}
                  type="button"
                  data-mol-id={`activity-filter-${type ?? 'all'}`}
                  aria-pressed={isActive}
                  onClick={() => setActiveType(type)}
                  style={pillStyle(isActive, isMobile)}
                >
                  {type !== null && (
                    // Themed SVG glyph from the IDE icon set (inherits the
                    // button's token color via currentColor) — never emoji.
                    <span aria-hidden="true" style={{ display: 'inline-flex' }}>
                      <Icon name={activityIconName(type)} size={14} />
                    </span>
                  )}
                  {label}
                </button>
              )
            })}

            {/* Refresh — right end of the filter row (the old header row that
                held it is gone with the duplicate title). */}
            <Tooltip
              content={t('activity.refresh', undefined, { defaultValue: 'Refresh activity' })}
              placement="bottom"
            >
              {/* mol-bespoke-button: icon-only square refresh affordance in the
                  panel's filter row, no label — sized to the 28px row rhythm with
                  a 40px touch floor on phones; every colour is a theme token. */}
              <button
                type="button"
                data-mol-id="activity-refresh"
                data-refreshing={refreshing ? 'true' : undefined}
                aria-label={t('activity.refresh', undefined, { defaultValue: 'Refresh activity' })}
                aria-busy={refreshing}
                disabled={refreshing}
                onClick={() => loadActivities({ silent: true })}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: isMobile ? 40 : 28,
                  height: isMobile ? 40 : 28,
                  padding: 0,
                  marginLeft: 'auto',
                  borderRadius: 6,
                  border: '1px solid var(--color-border-secondary)',
                  background: 'transparent',
                  color: 'inherit',
                  cursor: refreshing ? 'default' : 'pointer',
                  opacity: refreshing ? 0.7 : 1,
                  transition: 'background 100ms, border-color 100ms, opacity 100ms',
                }}
              >
                {refreshing ? <MoleculeSpinner size={14} /> : <Icon name="sync" size={14} />}
              </button>
            </Tooltip>
          </Flex>

          {/* Environment filter — dev captures and production sends share this
              list, so they have to be separable. */}
          <Flex align="center" gap="xs" wrap="wrap" style={{ marginBottom: 12 }}>
            {(
              [
                [null, t('activity.env.all', undefined, { defaultValue: 'All environments' })],
                ['dev', t('activity.env.dev', undefined, { defaultValue: 'Dev' })],
                ['prod', t('activity.env.prod', undefined, { defaultValue: 'Production' })],
              ] as Array<[ActivityScopeFilter, string]>
            ).map(([scope, label]) => (
              <button
                key={scope ?? 'all'}
                type="button"
                data-mol-id={`activity-env-${scope ?? 'all'}`}
                aria-pressed={activeScope === scope}
                onClick={() => setActiveScope(scope)}
                style={pillStyle(activeScope === scope, isMobile)}
              >
                {label}
              </button>
            ))}

            <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 6 }}>
              <Button
                size="sm"
                variant="outline"
                data-mol-id="activity-export"
                loading={exporting}
                disabled={exporting || visible.length === 0}
                onClick={handleExport}
              >
                {t('activity.export', undefined, { defaultValue: 'Export JSON' })}
              </Button>
              {confirmingClear ? (
                <>
                  <Button
                    size="sm"
                    variant="outline"
                    color="error"
                    data-mol-id="activity-clear-confirm"
                    loading={clearing}
                    disabled={clearing}
                    onClick={handleClear}
                  >
                    {t('activity.clearConfirm', undefined, { defaultValue: 'Delete them' })}
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    data-mol-id="activity-clear-cancel"
                    disabled={clearing}
                    onClick={() => setConfirmingClear(false)}
                  >
                    {t('common.cancel', undefined, { defaultValue: 'Cancel' })}
                  </Button>
                </>
              ) : canEdit !== false ? (
                <Button
                  size="sm"
                  variant="outline"
                  data-mol-id="activity-clear"
                  disabled={visible.length === 0}
                  onClick={() => setConfirmingClear(true)}
                >
                  {t('activity.clear', undefined, { defaultValue: 'Clear' })}
                </Button>
              ) : null}
            </span>
          </Flex>

          {confirmingClear && (
            <div
              role="alert"
              data-mol-id="activity-clear-warning"
              style={{ marginBottom: 10, fontSize: 12, color: 'var(--color-foreground-tertiary)' }}
            >
              {t('activity.clearWarning', undefined, {
                defaultValue: 'This deletes the captures shown here. It cannot be undone.',
              })}
            </div>
          )}

          {error && (
            <div
              style={{
                textAlign: 'center',
                padding: 12,
                fontSize: 13,
                color: 'var(--mol-color-error, #ef4444)',
                marginBottom: 12,
              }}
            >
              {error}
            </div>
          )}

          {loading ? (
            <Flex align="center" justify="center" className={cm.sp('py', 6)}>
              <MoleculeSpinner />
            </Flex>
          ) : visible.length === 0 ? (
            <div
              data-mol-id="activity-empty"
              data-empty-kind={emptyKind}
              style={{ textAlign: 'center', padding: 16, fontSize: 13, opacity: 0.6 }}
            >
              {/* An unwired channel's hint already explains why it's empty; the
                  generic line would just repeat "captured" twice. */}
              {emptyKind !== 'unwired' && (
                <div>
                  {t('activity.empty', undefined, { defaultValue: 'No captured activity yet.' })}
                </div>
              )}
              {emptyHint && (
                <div style={{ marginTop: emptyKind === 'unwired' ? 0 : 6, fontSize: 12 }}>
                  {emptyHint}
                </div>
              )}
            </div>
          ) : (
            <div data-mol-id="activity-list" data-total={total ?? undefined}>
              {visible.map((activity) => {
                const expanded = expandedId === activity.id
                const pill = activityStatusColors(activity.status)
                const sent = Boolean(activity.inboxSentAt)
                const age = formatActivityAge(activity.timestamp, nowMs)
                const exact = formatActivityTimestamp(activity.timestamp)
                const scope = activityScopeLabel(activity.environment)
                return (
                  <div
                    key={activity.id}
                    data-mol-id={`activity-row-${activity.id}`}
                    data-environment={activity.environment ?? undefined}
                    style={{ borderBottom: '1px solid var(--color-border)' }}
                  >
                    <Flex align="center" gap="xs">
                      {/* mol-bespoke-button: this is a LIST ROW, not a CTA — the
                          full-width expand/collapse hit area of one activity row.
                          A button box would draw a frame around every row. */}
                      <button
                        type="button"
                        data-mol-id={`activity-row-toggle-${activity.id}`}
                        aria-expanded={expanded}
                        onClick={() => setExpandedId(expanded ? null : activity.id)}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                          flex: 1,
                          minWidth: 0,
                          padding: '8px 0',
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          color: 'inherit',
                          textAlign: 'left',
                        }}
                      >
                        {/* Themed SVG glyph from the IDE icon set (inherits the
                            row's foreground via currentColor) — never emoji. */}
                        <span aria-hidden="true" style={{ display: 'inline-flex', flexShrink: 0 }}>
                          <Icon name={activityIconName(activity.type)} size={16} />
                        </span>
                        <span
                          style={{
                            flex: 1,
                            minWidth: 0,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            fontSize: 13,
                          }}
                        >
                          {activitySummaryLine(activity)}
                        </span>
                        {scope && (
                          <span
                            data-mol-id={`activity-env-badge-${activity.id}`}
                            style={{
                              flexShrink: 0,
                              fontSize: 10,
                              fontWeight: 600,
                              textTransform: 'uppercase',
                              letterSpacing: '0.04em',
                              padding: '2px 6px',
                              borderRadius: 4,
                              border: '1px solid var(--color-border-secondary)',
                              color: 'var(--color-foreground-tertiary)',
                            }}
                          >
                            {scope}
                          </span>
                        )}
                        {age && (
                          <span
                            data-mol-id={`activity-time-${activity.id}`}
                            title={exact ?? undefined}
                            style={{
                              flexShrink: 0,
                              fontSize: 11,
                              color: 'var(--color-foreground-tertiary)',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {age}
                          </span>
                        )}
                        <span
                          style={{
                            flexShrink: 0,
                            fontSize: 10,
                            fontWeight: 600,
                            textTransform: 'uppercase',
                            letterSpacing: '0.04em',
                            padding: '2px 7px',
                            borderRadius: 999,
                            color: pill.fg,
                            background: pill.bg,
                          }}
                        >
                          {activityStatusLabel(activity.status)}
                        </span>
                      </button>
                      <Tooltip
                        content={t('activity.delete', undefined, {
                          defaultValue: 'Delete this capture',
                        })}
                        placement="left"
                      >
                        {/* mol-bespoke-button: icon-only per-row delete, no label —
                            a quiet 26px glyph that must not outweigh the row it
                            sits in; 40px touch floor on phones, tokens throughout. */}
                        {canEdit !== false && (
                          <button
                            type="button"
                            data-mol-id={`activity-delete-${activity.id}`}
                            aria-label={t('activity.delete', undefined, {
                              defaultValue: 'Delete this capture',
                            })}
                            disabled={deletingId === activity.id}
                            onClick={() => handleDelete(activity)}
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexShrink: 0,
                              width: isMobile ? 40 : 26,
                              height: isMobile ? 40 : 26,
                              padding: 0,
                              borderRadius: 6,
                              border: '1px solid transparent',
                              background: 'transparent',
                              color: 'var(--color-foreground-tertiary)',
                              cursor: deletingId === activity.id ? 'default' : 'pointer',
                            }}
                          >
                            {deletingId === activity.id ? (
                              <MoleculeSpinner size={12} />
                            ) : (
                              <Icon name="trash" size={13} />
                            )}
                          </button>
                        )}
                      </Tooltip>
                    </Flex>

                    {expanded && (
                      <div style={{ padding: '4px 0 12px' }}>
                        <ActivityDetail activity={activity} />
                        {activity.type === 'email' && (
                          <div
                            style={{ marginTop: 10 }}
                            data-mol-id={`activity-send-inbox-${activity.id}`}
                          >
                            {canEdit !== false && (
                              <Button
                                size="sm"
                                variant="outline"
                                data-mol-id={`activity-send-inbox-button-${activity.id}`}
                                loading={sendingId === activity.id}
                                disabled={sent || sendingId === activity.id}
                                onClick={() => handleSendToInbox(activity)}
                              >
                                {sent
                                  ? t('activity.sendToInbox.sent', undefined, {
                                      defaultValue: 'Sent to your inbox',
                                    })
                                  : t('activity.sendToInbox', undefined, {
                                      defaultValue: 'Send to my inbox',
                                    })}
                              </Button>
                            )}
                            {sendErrors[activity.id] && (
                              <div
                                role="alert"
                                data-mol-id={`activity-send-error-${activity.id}`}
                                style={{
                                  marginTop: 6,
                                  fontSize: 12,
                                  color: 'var(--mol-color-error, #ef4444)',
                                }}
                              >
                                {sendErrors[activity.id]}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )
              })}

              {hasMore && (
                <div style={{ textAlign: 'center', paddingTop: 12 }}>
                  <Button
                    size="sm"
                    variant="outline"
                    data-mol-id="activity-load-more"
                    loading={loadingMore}
                    disabled={loadingMore}
                    onClick={loadMore}
                  >
                    {t('activity.loadMore', undefined, { defaultValue: 'Load older' })}
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* Retention is a promise the product has to be able to repeat, so it
              is stated where the data is. Matches the server default in
              App/Resource/Activity/retention.ts. */}
          <div
            data-mol-id="activity-retention-note"
            style={{
              marginTop: 16,
              fontSize: 11,
              lineHeight: 1.45,
              color: 'var(--color-foreground-tertiary)',
            }}
          >
            {t('activity.retention', undefined, {
              defaultValue: 'Captures are kept for 30 days, up to 500 per project.',
            })}
            {total != null &&
              ` ${t('activity.total', { count: total }, { defaultValue: '{{count}} stored now.' })}`}
          </div>
        </>
      )}
    </div>
  )
}
